/**
 * Notification Service
 * Global notification handling with custom event emitter pattern (Vue 3 compatible)
 */

// Simple event emitter for Vue 3 (replaces Vue 2 event bus)
class EventEmitter {
  constructor() {
    this.events = {};
  }
  
  $on(event, callback) {
    if (!this.events[event]) {
      this.events[event] = [];
    }
    this.events[event].push(callback);
  }
  
  $off(event, callback) {
    if (!this.events[event]) return;
    if (callback) {
      this.events[event] = this.events[event].filter(cb => cb !== callback);
    } else {
      delete this.events[event];
    }
  }
  
  $emit(event, data) {
    if (!this.events[event]) return;
    this.events[event].forEach(callback => callback(data));
  }
}

// Create event bus for notifications
const NotificationBus = new EventEmitter();

/**
 * Notification types
 */
export const NotificationType = {
  SUCCESS: 'success',
  ERROR: 'error',
  WARNING: 'warning',
  INFO: 'info'
};

/**
 * Default durations by type (in ms)
 */
const DEFAULT_DURATIONS = {
  success: 4000,
  error: 6000,
  warning: 5000,
  info: 4000
};

/**
 * Notification Service
 */
const NotificationService = {
  /**
   * Show a notification
   * @param {string} message - Message to display
   * @param {string} type - Notification type (success, error, warning, info)
   * @param {number} duration - Duration in ms (optional)
   */
  show(message, type = NotificationType.INFO, duration = null) {
    NotificationBus.$emit('notification', {
      message,
      type,
      duration: duration || DEFAULT_DURATIONS[type] || 4000
    });
  },

  /**
   * Show success notification
   * @param {string} message
   * @param {number} duration
   */
  success(message, duration = null) {
    this.show(message, NotificationType.SUCCESS, duration);
  },

  /**
   * Show error notification
   * @param {string} message
   * @param {number} duration
   */
  error(message, duration = null) {
    this.show(message, NotificationType.ERROR, duration);
  },

  /**
   * Show warning notification
   * @param {string} message
   * @param {number} duration
   */
  warning(message, duration = null) {
    this.show(message, NotificationType.WARNING, duration);
  },

  /**
   * Show info notification
   * @param {string} message
   * @param {number} duration
   */
  info(message, duration = null) {
    this.show(message, NotificationType.INFO, duration);
  },

  /**
   * Subscribe to notifications
   * @param {Function} callback - Function to call when notification is emitted
   * @returns {Function} - Unsubscribe function
   */
  subscribe(callback) {
    NotificationBus.$on('notification', callback);
    return () => NotificationBus.$off('notification', callback);
  }
};

// Make it available as Vue 3 plugin
NotificationService.install = function(app) {
  app.config.globalProperties.$notify = NotificationService;
};

export { NotificationBus };
export default NotificationService;
